import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd, yieldToMainThread, isMobileBreakpoint } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} [productImage] - Product image element
 * @property {HTMLElement[]} [swatches] - Swatches for variant selection
 * @property {HTMLElement[]} [images] - Variant images
 * @property {HTMLElement[]} [shipTexts] - Shipping/Stock texts
 * @property {HTMLDialogElement} [stickyMain] - Variant selection sheet, modal below 992px
 * @property {HTMLElement} [variantActiveText] - Active variant text display
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
    requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

    /** @type {IntersectionObserver | null} */
    #buyButtonsIntersectionObserver = null;

    /** @type {IntersectionObserver | null} */
    #mainBottomObserver = null;

    /** @type {number | undefined} */
    #resetTimeout;

    /** @type {boolean} */
    #isStuck = false;

    /** @type {number | null} */
    #animationTimeout = null;

    /** @type {AbortController} */
    #abortController = new AbortController();

    /** @type {HTMLButtonElement | null} */
    #targetAddToCartButton = null;

    /** @type {number} */
    #currentQuantity = 1;

    /** @type {boolean} */
    #hiddenByBottom = false;

    /** @type {boolean} */
    #buyButtonsAboveViewport = false;

    /** Assumed in view until the observer reports otherwise, so a missing observer never pins the bar. */
    #buyButtonsInView = true;

    /** @type {number} */
    #lastScrollY = 0;

    /** Matches the CSS breakpoint below which the variant list becomes a bottom sheet. */
    #sheetMediaQuery = matchMedia('(max-width: 991px)');

    connectedCallback() {
        super.connectedCallback();

        this.#setupIntersectionObserver();

        const { signal } = this.#abortController;

        this.addEventListener('click', this.#handleSheetBackdropClick, { signal });
        // `close` does not bubble, so it is caught in the capture phase to survive morphs of the dialog.
        this.addEventListener('close', this.#syncVariantToggle, { signal, capture: true });
        this.#sheetMediaQuery.addEventListener('change', () => this.closeVariantSheet(), { signal });

        const target = this.closest('.shopify-section');
        target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
        target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

        document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
        document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
        document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

        this.#lastScrollY = window.scrollY;
        document.addEventListener('scroll', this.#handleScroll, { signal, passive: true });

        this.#getInitialQuantity();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#buyButtonsIntersectionObserver?.disconnect();
        this.#mainBottomObserver?.disconnect();
        this.#abortController.abort();
        if (this.#animationTimeout) {
            clearTimeout(this.#animationTimeout);
        }
        clearTimeout(this.#resetTimeout);
    }

    /**
     * Sets up the IntersectionObserver to watch the buy buttons visibility
     */
    #setupIntersectionObserver() {
        const productForm = this.#getProductForm();
        if (!productForm) return;

        const buyButtonsBlock = productForm.closest('.buy-buttons-block');
        if (!buyButtonsBlock) return;

        // In themes migrated from 2.0, the footer element doesn't exist
        const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
        if (!footer) return;

        // Observer for buy buttons visibility. On mobile it also reveals the bar, since the buy
        // buttons start below the fold and no scroll event is needed to know they are out of reach.
        this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
            const [entry] = entries;
            if (!entry) return;

            this.#buyButtonsInView = entry.isIntersecting;

            if (!entry.isIntersecting) {
                const rect = entry.target.getBoundingClientRect();
                this.#buyButtonsAboveViewport = rect.bottom < 0 || rect.top < 0;

                if (isMobileBreakpoint() && !this.#isStuck && !this.#hiddenByBottom) {
                    this.#showStickyBar();
                }
            } else {
                this.#buyButtonsAboveViewport = false;
                if (this.#isStuck) {
                    this.#hiddenByBottom = false;
                    this.#hideStickyBar();
                }
            }
        });

        // Observer for footer visibility - hides sticky bar at page bottom
        this.#mainBottomObserver = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (!entry) return;

                if (entry.isIntersecting && this.#isStuck) {
                    this.#hiddenByBottom = true;
                    this.#hideStickyBar();
                } else if (!entry.isIntersecting && this.#hiddenByBottom) {
                    this.#hiddenByBottom = false;
                }
            },
            {
                rootMargin: '200px 0px 0px 0px',
            }
        );

        this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
        this.#mainBottomObserver.observe(footer);
        this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    // Public action handlers
    /**
     * Handles the add to cart button click in the sticky bar
     */
    handleAddToCartClick = async () => {
        if (!this.#targetAddToCartButton) return;
        this.#targetAddToCartButton.dataset.puppet = 'true';
        this.#targetAddToCartButton.click();

        const { addToCartButton } = this.refs;
        if (!addToCartButton) return;

        if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

        addToCartButton.dataset.added = 'true';

        // Cross-component lookup: the cart icon lives in the header, outside this component's subtree.
        const cartIcon = document.querySelector('.header-actions__cart-icon');
        const visibleImageEl = /** @type {HTMLImageElement | null} */ (
            (this.refs.images || []).find((el) => el.style.display !== 'none')?.querySelector('img') ??
                this.refs.stickyBar?.querySelector('.customize-product__image:not([style*="display: none"]) img')
        );

        /** @type {(HTMLElement | FlyToCart)[]} */
        const elementsToAwait = [addToCartButton];

        if (cartIcon && visibleImageEl?.src) {
            const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
            flyToCartElement.classList.add('fly-to-cart--sticky');
            flyToCartElement.style.setProperty('background-image', `url(${visibleImageEl.src})`);
            flyToCartElement.style.setProperty('--start-opacity', '0');
            flyToCartElement.source = visibleImageEl;
            flyToCartElement.destination = cartIcon;
            document.body.appendChild(flyToCartElement);
            elementsToAwait.push(flyToCartElement);
        }

        await yieldToMainThread();
        await onAnimationEnd(elementsToAwait);

        this.#resetTimeout = setTimeout(() => {
            addToCartButton.removeAttribute('data-added');
        }, 800);
    };

    /**
     * Handles swatch selection from the radio change event.
     * @param {Event} event
     */
    handleSwatchChange(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.checked) return;
        const swatch = input.labels?.[0] ?? this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (swatch instanceof HTMLElement) this.selectSwatch(swatch);
    }

    /**
     * Selects the matching base in the main variant picker, which re-renders this bar.
     * @param {Event} event
     */
    handleBaseChange(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.checked) return;

        const mainInput = document.querySelector(
            `variant-picker[data-product-id="${this.dataset.productId}"] [data-base-option] input[value="${CSS.escape(input.value)}"]`
        );
        if (!(mainInput instanceof HTMLInputElement) || mainInput.checked) return;

        mainInput.checked = true;
        mainInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handleToggleButtonClick() {
        const { stickyMain } = this.refs;
        if (!stickyMain) return;

        if (stickyMain.open) {
            this.closeVariantSheet();
        } else if (this.#sheetMediaQuery.matches) {
            stickyMain.showModal();
            this.#syncVariantToggle();
        }
    }

    closeVariantSheet() {
        const { stickyMain } = this.refs;
        if (stickyMain?.open) stickyMain.close();
    }

    /**
     * A click whose target is the dialog itself landed on the backdrop, since the inner wrapper fills the sheet.
     * @param {MouseEvent} event
     */
    #handleSheetBackdropClick = (event) => {
        if (event.target === this.refs.stickyMain) this.closeVariantSheet();
    };

    #syncVariantToggle = () => {
        const toggle = this.querySelector('.sticky-product-variants');
        toggle?.setAttribute('aria-expanded', String(Boolean(this.refs.stickyMain?.open)));
    };

    /**
     * Synchronizes the selected swatch with the UI and the main variant picker.
     * @param {HTMLElement} swatch
     */
    selectSwatch(swatch) {
        const { index, name, varid: varId } = swatch.dataset;
        if (!index || !varId) return;

        // 1. Update Images - find directly in component for robustness
        const images = this.querySelectorAll('.customize-product__image');
        images.forEach((img) => {
            /** @type {HTMLElement} */ (img).style.display = 'none';
        });

        const activeImg = this.querySelector(`.customize-product__image[data-index-${index}]`);
        if (activeImg) {
            /** @type {HTMLElement} */ (activeImg).style.display = '';
        }

        // 2. Update Swatch Selection State
        const swatches = this.querySelectorAll('.customize-product__swatch');
        swatches.forEach((s) => s.classList.remove('selectedColor'));
        swatch.classList.add('selectedColor');

        // 3. Update Active Variant Text
        const variantActiveText = this.querySelector('.product-form__variant-active p');
        if (variantActiveText) {
            variantActiveText.textContent = name || '';
        }

        // 4. Update Shipping/Stock Text
        const shipTexts = this.querySelectorAll('.stock-status');
        shipTexts.forEach((el) => {
            if (/** @type {HTMLElement} */ (el).dataset.id === varId) {
                el.classList.remove('hidden');
            } else {
                el.classList.add('hidden');
            }
        });

        // 5. Update Component Attributes
        this.setAttribute('data-selected-variant', varId);
        this.setAttribute('data-option-value', name || '');

        // 6. Sync with Main Variant Picker
        const mainVariantPicker = document.querySelector('variant-picker');
        if (mainVariantPicker) {
            const mainInput = mainVariantPicker.querySelector(`input[data-variant-id="${varId}"]`);
            if (mainInput instanceof HTMLInputElement) {
                mainInput.checked = true;
                mainInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }

    /**
     * Handles variant update events
     * @param {CustomEvent} event - The variant update event
     */
    #handleVariantUpdate = (event) => {
        if (event.detail.data.productId !== this.dataset.productId) return;

        const variant = event.detail.resource;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        if (this.refs.stickyMain?.open) {
            newStickyBar.querySelector('.sticky-variants-main')?.setAttribute('open', '');
            newStickyBar.querySelector('.sticky-product-variants')?.setAttribute('aria-expanded', 'true');
        }

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
            this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
            this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
            this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
    };

    /**
     * Handles variant selected events
     * @param {CustomEvent} event - The variant selected event
     */
    #handleVariantSelected = (event) => {
        // The variant update event will follow and handle all updates via morph
        // We just update the dataset here for tracking
        const variantId = event.detail.resource?.id;
        if (!variantId) return;
        this.dataset.currentVariantId = variantId;
    };

    /**
     * Updates the variant title based on selected options when the variant is unavailable
     */
    #handleVariantUnavailable = () => {
        this.dataset.currentVariantId = '';
        const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
        const productId = this.dataset.productId;
        const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
        if (!variantTitleElement || !variantPicker) return;

        const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
            .map((option) => /** @type {HTMLInputElement} */ (option).value)
            .filter((value) => value !== '')
            .join(' / ');
        if (!selectedOptions) return;
        variantTitleElement.textContent = selectedOptions;
    };

    /**
     * Handles cart add complete (success or error) - resets puppet flag
     * @param {CustomEvent} _event - The cart event (unused)
     */
    #handleCartAddComplete = (_event) => {
        // Reset the puppet flag after cart operation
        if (this.#targetAddToCartButton) {
            this.#targetAddToCartButton.dataset.puppet = 'false';
        }
    };

    /**
     * Handles quantity selector update events
     * @param {QuantitySelectorUpdateEvent} event - The quantity update event
     */
    #handleQuantityUpdate = (event) => {
        // Only respond to product page quantity selector updates, not cart drawer
        if (event.detail.cartLine) return;

        this.#currentQuantity = event.detail.quantity;
        this.#updateButtonText();
    };

    /**
     * On mobile the bar stands in for the buy buttons whenever they are out of view, above or below.
     * On desktop it only comes back when scrolling up past them, so a freshly loaded page stays clear.
     */
    #handleScroll = () => {
        const currentScrollY = window.scrollY;
        const scrollingUp = currentScrollY < this.#lastScrollY;
        this.#lastScrollY = currentScrollY;

        if (isMobileBreakpoint()) {
            if (!this.#buyButtonsInView && !this.#isStuck && !this.#hiddenByBottom) {
                this.#showStickyBar();
            }
        } else {
            if (scrollingUp && this.#buyButtonsAboveViewport && !this.#isStuck && !this.#hiddenByBottom) {
                this.#showStickyBar();
            } else if (!scrollingUp && this.#isStuck) {
                this.#hideStickyBar();
            }
        }
    };

    /**
     * Shows the sticky bar with animation
     */
    #showStickyBar() {
        const { stickyBar } = this.refs;
        this.#isStuck = true;
        stickyBar.removeAttribute('inert');
        stickyBar.dataset.stuck = 'true';
    }

    /**
     * Hides the sticky bar with animation
     */
    #hideStickyBar() {
        const { stickyBar } = this.refs;
        this.#isStuck = false;
        stickyBar.dataset.stuck = 'false';
        onAnimationEnd(
            stickyBar,
            () => {
                if (this.#isStuck) return;
                stickyBar.setAttribute('inert', '');
            },
            { subtree: false }
        );
    }

    // Helper methods
    /**
     * Gets the product form element
     * @returns {HTMLElement | null}
     */
    #getProductForm() {
        const productId = this.dataset.productId;
        if (!productId) return null;

        const sectionElement = this.closest('.shopify-section');
        if (!sectionElement) return null;

        const sectionId = sectionElement.id.replace('shopify-section-', '');
        return document.querySelector(
            `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
        );
    }

    /**
     * Gets the initial quantity from the data attribute
     */
    #getInitialQuantity() {
        this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
        this.#updateButtonText();
    }

    /**
     * Updates the button text to include quantity
     */
    #updateButtonText() {
        const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

        const available = !addToCartButton.disabled;

        // Update the quantity number
        quantityNumber.textContent = this.#currentQuantity.toString();

        // Show/hide the quantity display based on availability and quantity
        if (available && this.#currentQuantity > 1) {
            quantityDisplay.style.display = 'inline';
        } else {
            quantityDisplay.style.display = 'none';
        }
    }
}

if (!customElements.get('sticky-add-to-cart')) {
    customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
